import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  ShoppingBag, 
  Clock, 
  Package, 
  CheckCircle,
  ArrowRight,
  Zap,
  RefreshCw,
  BarChart3,
  Users,
  Globe
} from "lucide-react";

const ShopifyFulfillmentPage = () => {
  const features = [
    {
      icon: Zap,
      title: "Automatische Bestellübernahme",
      description: "Neue Bestellungen aus Ihrem Shopify-Shop werden automatisch importiert und verarbeitet."
    },
    {
      icon: Clock,
      title: "Same-Day Versand bis 12 Uhr",
      description: "Bestellungen bis 12 Uhr werden noch am selben Tag kommissioniert und versendet."
    },
    {
      icon: Package,
      title: "Ab 20 Sendungen/Monat",
      description: "Kein Mindestvolumen nötig. Perfekt für Startups und wachsende Online-Shops."
    },
    {
      icon: RefreshCw,
      title: "Echtzeit-Synchronisation",
      description: "Bestände, Tracking-Nummern und Versandstatus werden automatisch in Shopify aktualisiert."
    },
    {
      icon: BarChart3,
      title: "Bestandsmanagement",
      description: "Übersichtliches Dashboard mit Echtzeit-Bestandsanzeige und Nachbestellhinweisen."
    },
    {
      icon: Users,
      title: "Persönlicher Support",
      description: "Fester Ansprechpartner, der Ihren Shop und Ihre Produkte kennt."
    }
  ];

  const process = [
    {
      step: "1",
      title: "Shop verbinden",
      description: "Einfache Anbindung Ihres Shopify-Shops über API oder App."
    },
    {
      step: "2",
      title: "Produkte einlagern",
      description: "Senden Sie Ihre Produkte zu uns oder wir holen sie ab."
    },
    {
      step: "3",
      title: "Automatisch versenden",
      description: "Wir übernehmen Pick, Pack & Ship für jede Bestellung."
    }
  ];

  const benefits = [
    "Keine Mindestlaufzeit",
    "Transparente Preise",
    "Kostenlose Onboarding-Beratung",
    "Same-Day Versand bis 12 Uhr",
    "Versand in 180 Länder",
    "Retourenmanagement inklusive"
  ];

  return (
    <div data-testid="shopify-fulfillment-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#96bf48] to-[#5c8e1a] text-white py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <ShoppingBag size={32} />
              <span className="text-lg font-bold uppercase tracking-wider">Shopify Fulfillment</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Shopify Fulfillment ab <span className="text-white/90">20 Bestellungen</span>/Monat
            </h1>
            <p className="text-xl text-white/90 mb-8 max-w-2xl">
              Nahtlose Integration mit Ihrem Shopify-Shop. Automatische Bestellverarbeitung, 
              Same-Day Versand und persönlicher Support – alles inklusive.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/angebot">
                <Button className="bg-white text-[#5c8e1a] hover:bg-slate-100 uppercase tracking-wider font-bold px-10 py-6 text-lg">
                  Kostenloses Angebot anfordern
                </Button>
              </Link>
              <Link to="/kontakt">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#5c8e1a] uppercase tracking-wider font-bold px-10 py-6 text-lg bg-transparent">
                  Demo vereinbaren
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* USP Bar */}
      <section className="bg-slate-900 text-white py-6">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 text-center">
            <div className="flex items-center gap-2">
              <CheckCircle size={20} className="text-green-400" />
              <span className="font-bold">Ab 20 Sendungen/Monat</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle size={20} className="text-green-400" />
              <span className="font-bold">Same-Day bis 12 Uhr</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle size={20} className="text-green-400" />
              <span className="font-bold">Automatische Synchronisation</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle size={20} className="text-green-400" />
              <span className="font-bold">180 Länder weltweit</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-orange-600 uppercase tracking-widest font-bold text-sm">
              Features
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mt-4 text-slate-900">
              Shopify-Integration, die funktioniert
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={feature.title}
                  className="p-8 border border-slate-200 hover:border-orange-300 hover:shadow-lg transition-all"
                >
                  <div className="w-14 h-14 bg-orange-100 flex items-center justify-center mb-6">
                    <Icon className="text-orange-600 w-7 h-7" />
                  </div>
                  <h3 className="font-bold text-xl mb-3 text-slate-900">{feature.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-orange-600 uppercase tracking-widest font-bold text-sm">
              So funktioniert's
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mt-4 text-slate-900">
              In 3 Schritten zum Shopify Fulfillment
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {process.map((item, index) => (
              <div key={item.step} className="text-center relative">
                <div className="w-20 h-20 bg-orange-600 text-white text-3xl font-bold rounded-full flex items-center justify-center mx-auto mb-6">
                  {item.step}
                </div>
                <h3 className="font-bold text-xl mb-3 text-slate-900">{item.title}</h3>
                <p className="text-slate-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-orange-600 uppercase tracking-widest font-bold text-sm">
                Ihre Vorteile
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mt-4 mb-8 text-slate-900">
                Warum Kauz Logistik für Shopify?
              </h2>
              
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="text-orange-600 flex-shrink-0" size={20} />
                    <span className="text-slate-700 font-medium">{benefit}</span>
                  </div>
                ))}
              </div>

              <div className="bg-slate-50 p-6 border-l-4 border-orange-600 mb-8">
                <p className="text-slate-700 italic">
                  "Wir verstehen die Herausforderungen von Shopify-Händlern. Deshalb bieten wir 
                  flexible Lösungen ohne Mindestlaufzeit – Sie zahlen nur, was Sie brauchen."
                </p>
                <p className="text-slate-900 font-bold mt-3">– Waldemar Kauz, Geschäftsführer</p>
              </div>

              <Link to="/angebot">
                <Button className="bg-orange-600 hover:bg-orange-700 text-white uppercase tracking-wider font-bold px-8">
                  Angebot für Shopify anfordern
                  <ArrowRight className="ml-2" size={16} />
                </Button>
              </Link>
            </div>
            
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_3f8853ef-18d6-476f-b97c-eda985cb2340/artifacts/63u2wcg1_ecommerce_2.jpg"
                alt="Shopify E-Commerce"
                className="w-full shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Also Supported */}
      <section className="py-16 bg-slate-100">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-xl font-bold text-slate-700 mb-8">
            Wir unterstützen auch andere Plattformen:
          </h3>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
            <span className="text-slate-600 font-bold text-lg">WooCommerce</span>
            <span className="text-slate-600 font-bold text-lg">Amazon</span>
            <span className="text-slate-600 font-bold text-lg">eBay</span>
            <span className="text-slate-600 font-bold text-lg">Wix</span>
            <span className="text-slate-600 font-bold text-lg">+ weitere</span>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <ShoppingBag size={48} className="mx-auto mb-6 text-orange-500" />
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Bereit für professionelles Shopify Fulfillment?
          </h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Fordern Sie jetzt Ihr kostenloses Angebot an. 
            Wir zeigen Ihnen, wie einfach die Integration funktioniert.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/angebot">
              <Button className="bg-orange-600 hover:bg-orange-700 text-white uppercase tracking-wider font-bold px-12 py-6 text-lg">
                Kostenloses Angebot anfordern
              </Button>
            </Link>
            <Link to="/kontakt">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-slate-900 uppercase tracking-wider font-bold px-12 py-6 text-lg bg-transparent">
                Fragen? Kontaktieren Sie uns
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ShopifyFulfillmentPage;
